<template>
  <div class="common-social-share-platforms">
    <ElementsAvatar :icon-src="shareIcon" icon-alt="Parking icon" @click="share()" />

    <div class="common-social-share-platform__platform-select-wrapper" :class="{'--is-visible': isPlatformSelectionOpen}">
      <div class="platform-select-wrapper__inner">
        <div class="platform-select-wrapper__inner__title">
          Select to share
        </div>
        <div class="platform-select-wrapper__platforms-list">
          <div v-for="(platform, key) in supportedPlatforms" :key="`platform-${key}`" class="platform-select-wrapper__platforms-list-item">
            <img v-lazy-load  :data-src="getPlatformIcon(platform)" :alt="`${platform} icon`" @click="setPreferredPlatform(platform)">
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed, ComputedRef, Ref} from 'vue';
import shareIcon from 'assets/images/icons/share.webp';
import facebookIcon from './icons/facebook.webp';
import linkedinIcon from './icons/linkedin.webp';
import twitterIcon from './icons/twitter.webp';
import whatsappIcon from './icons/whatsapp.webp';

export type SocialShareSupportedPlatforms = 'facebook' | 'twitter' | 'linkedin' | 'whatsapp';

// PROPS Definitions
//----------------------------------------------------------------------------------------------------------------------
const props = defineProps({
  url: {
    type: String,
    required: true,
  },
});

// DATA Definitions
//----------------------------------------------------------------------------------------------------------------------
const preferredPlatform: Ref<SocialShareSupportedPlatforms | null> = ref(null);
const isPlatformSelectionOpen: Ref<boolean> = ref(false);
const supportedPlatforms: Ref<SocialShareSupportedPlatforms[]> = ref(['facebook', 'twitter', 'linkedin', 'whatsapp'] as SocialShareSupportedPlatforms[]);

/**
 * Share
 */
async function share () {
  try {
    // Check if params are valid
    if (!props.url) {
      return;
    }

    // Check if preferred platform is supported
    if (isMobileShareSupported.value) {
      navigator.share({url: props.url});
      return;
    }

    if (!preferredPlatform.value) {
      isPlatformSelectionOpen.value = !isPlatformSelectionOpen.value;
      return;
    }

    // Share using manual share method
    switch (preferredPlatform.value) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${props.url}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?url=${props.url}`, '_blank');
        break;
      case 'linkedin':
        window.open(`https://www.linkedin.com/shareArticle?mini=true&url=${props.url}`, '_blank');
        break;
      case 'whatsapp':
        window.open(`https://api.whatsapp.com/send?text=${props.url}`, '_blank');
        break;
    }
    preferredPlatform.value = null;
    return;
  } catch (error) {
    console.log(error);
  }
}

/**
 * Set preferred platform
 *
 * @param platform
 */
function setPreferredPlatform (platform: SocialShareSupportedPlatforms) {
  preferredPlatform.value = platform;
  share();
  isPlatformSelectionOpen.value = false;
}

/**
 * Check whether the mobile share is supported or not
 */
const isMobileShareSupported: ComputedRef<boolean> = computed(() => {
  return (navigator as any) && navigator.share;
});

/**
 * Get platform icon
 *
 * @param platform
 */
function getPlatformIcon (platform: SocialShareSupportedPlatforms) {
  switch (platform) {
    case 'facebook':
      return facebookIcon;
    case 'twitter':
      return twitterIcon;
    case 'linkedin':
      return linkedinIcon;
    case 'whatsapp':
      return whatsappIcon;
  }
}
</script>

<style lang="scss">
.common-social-share-platforms {
  position: relative;

  .el-avatar {
    .__el-avatar-inner {
      scale: 1;
      transition: all 0.2s ease-in-out;
      cursor: pointer;

      &:hover {
        scale: 1.1;
        transition: all 0.2s ease-in-out;
      }
    }
  }

  .common-social-share-platform__platform-select-wrapper {
    position: fixed;
    width: 0;
    max-height: 0;
    top: 45px;
    right: 0;
    overflow: hidden;
    z-index: 99;
    box-shadow: 1px 1px 10px 4px #00000038;
    border-radius: 12px;
    opacity: 0;
    transform: scale(0);
    transition: all 0.5s ease-in-out;

    .platform-select-wrapper__inner {
      padding: 12px;
      width: 100%;
      height: 100%;
      background: #fff;

      .platform-select-wrapper__inner__title {
        width: 100%;
        text-align: left;
        color: rgb(var(--primary-dark));
        font-weight: 500;
      }

      .platform-select-wrapper__platforms-list {
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
        margin-top: 12px;

        .platform-select-wrapper__platforms-list-item {
          padding: 6px;

          img {
            width: 40px;
            height: 40px;
            margin: 4px;
            cursor: pointer;
            transition: all 0.2s ease-in-out;

            &:hover {
              transform: scale(1.1);
              transition: all 0.2s ease-in-out;
            }
          }
        }
      }
    }

    &.--is-visible {
      width: 280px;
      max-height: fit-content;
      opacity: 1;
      transform: scale(1);
      transition: all 0.2s ease-in-out;
    }
  }
}
</style>
